import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const GlowWord = ({ children, color = 'gold' }) => {
  const colors = {
    gold: { base: 'rgba(212,175,55,0.8)', glow: '0 0 15px rgba(212,175,55,0.9), 0 0 40px rgba(212,175,55,0.5)' },
    wine: { base: 'rgba(155,66,78,0.9)', glow: '0 0 15px rgba(155,66,78,0.9), 0 0 40px rgba(155,66,78,0.5)' },
  };
  return (
    <motion.span
      className="font-serif italic cursor-default inline-block"
      style={{ color: colors[color].base }}
      whileHover={{ textShadow: colors[color].glow }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.span>
  );
};

const GrandIllusion = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imageFilter = useTransform(
    scrollYProgress,
    [0, 0.4, 1],
    ['grayscale(100%) brightness(0.4)', 'grayscale(50%) brightness(0.65)', 'grayscale(0%) brightness(0.9)']
  );

  return (
    <section ref={ref} className="relative bg-dark border-t border-white/5 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[90vh]">
        <div className="relative lg:sticky lg:top-0 lg:h-screen overflow-hidden order-2 lg:order-1">
          <motion.img
            src="https://media-manager-c.questera.ai/greta-media/8c84f85263efda659889de32f1f32e795be69e9a2504f254db44deecf03d5d2eaf8d8138b6c4ad97d7d004f4d5618742/images/aW1hZ2UvcG5n/0a8dbd1ab6196d088ee699a1d0e6492e.png"
            alt="Jennifer Gräser"
            style={{ filter: imageFilter }}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark lg:block hidden" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent" />
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.07), transparent 60%)' }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-8 md:px-14 py-28 space-y-10 order-1 lg:order-2">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold tracking-[0.3em] uppercase text-xs font-semibold"
          >
            Die Große Illusion
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-4xl md:text-5xl font-serif text-cream leading-tight"
          >
            Das Problem ist nicht er.
            <span className="italic text-wine-light block mt-2">
              Das Problem ist nicht der Markt.
            </span>
          </motion.h2>

          {[
            { delay: 0.2, text: 'Das Problem ist eine unsichtbare, aber mächtige Kraft: deine energetische Signatur. Dein inneres Betriebssystem, das seit der Kindheit darauf programmiert wurde, Liebe zu verdienen statt sie zu empfangen. Erfolg zu erkämpfen statt ihn anzuziehen. Sicherheit im Außen zu suchen statt in dir selbst zu verankern.' },
            { delay: 0.4, highlight: true, text: 'Es gibt zwei Prinzipien, die in jedem von uns wohnen: das maskuline Prinzip des Tuns, Kontrollierens, Erreichens – und das feminine Prinzip des Seins, Empfangens, Vertrauens. Die meisten Frauen, die zu mir kommen, haben das maskuline Prinzip so tief verinnerlicht, dass sie vergessen haben, wie es sich anfühlt, einfach zu existieren, ohne zu leisten.' },
            { delay: 0.6, text: 'In der Liebe zeigt sich das als ewiges Warten, Analysieren, Anpassen. Im Business als Hustle ohne Ende, als Unfähigkeit, Preise zu erhöhen, als Gefühl, nie genug zu sein. Beides hat dieselbe Wurzel: ein Nervensystem, das gelernt hat, dass Ruhe gefährlich ist. Dass Sichtbarkeit Ablehnung bedeutet. Dass Empfangen Schuld erzeugt.' },
            { delay: 0.8, gold: true, text: 'Wenn wir diese Wurzel transformieren – nicht reparieren, nicht überdecken, sondern wirklich transformieren – verändert sich alles. Nicht weil du etwas anderes tust. Sondern weil du jemand anderes bist.' },
          ].map((block, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: block.delay, duration: 0.8 }}
              className={`font-light leading-relaxed text-lg ${block.gold ? 'text-gold font-serif italic text-xl' : block.highlight ? 'text-cream font-serif italic' : 'text-cream/65'}`}
            >
              {block.text}
            </motion.p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GrandIllusion;